import { MdDarkMode, MdLightMode, MdSettingsBrightness } from 'react-icons/md';
import { useTheme, type ThemePreference } from '../context/ThemeContext';
import { cn } from '../lib/utils';

const OPTIONS: Array<{
  value: ThemePreference;
  label: string;
  icon: typeof MdLightMode;
  title: string;
}> = [
  { value: 'light', label: 'Light', icon: MdLightMode, title: 'Light theme' },
  { value: 'dark', label: 'Dark', icon: MdDarkMode, title: 'Dark theme' },
  { value: 'auto', label: 'Auto', icon: MdSettingsBrightness, title: 'Match system theme' },
];

/**
 * Compact professional theme switcher for the header.
 * Cycles Light → Dark → Auto, or open as a mini segmented control on wider screens.
 */
export function ThemeToggle({ compact = true }: { compact?: boolean }) {
  const { theme, resolvedTheme, setTheme } = useTheme();

  if (compact) {
    const next: ThemePreference =
      theme === 'light' ? 'dark' : theme === 'dark' ? 'auto' : 'light';
    const Icon =
      theme === 'auto' ? MdSettingsBrightness : resolvedTheme === 'dark' ? MdDarkMode : MdLightMode;

    return (
      <button
        type="button"
        onClick={() => setTheme(next)}
        className={cn(
          'group relative inline-flex h-9 w-9 items-center justify-center rounded-xl border transition-all duration-200',
          'border-[hsl(var(--border))] bg-[hsl(var(--card))] text-[hsl(var(--muted-foreground))]',
          'hover:border-[#0088cc]/40 hover:bg-[#0088cc]/8 hover:text-[#0088cc]',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0088cc]/35'
        )}
        aria-label={`Theme: ${theme}. Click for ${next}.`}
        title={`Theme: ${theme === 'auto' ? `Auto (${resolvedTheme})` : theme}`}
      >
        <Icon className="h-[18px] w-[18px] transition-transform duration-200 group-hover:scale-110" />
        {theme === 'auto' && (
          <span className="absolute -bottom-0.5 -right-0.5 h-2 w-2 rounded-full border border-[hsl(var(--card))] bg-[#0088cc]" />
        )}
      </button>
    );
  }

  return (
    <div
      className="inline-flex items-center rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/60 p-1"
      role="radiogroup"
      aria-label="Color theme"
    >
      {OPTIONS.map(({ value, label, icon: Icon, title }) => {
        const active = theme === value;
        return (
          <button
            key={value}
            type="button"
            role="radio"
            aria-checked={active}
            title={title}
            onClick={() => setTheme(value)}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-semibold transition-all',
              active
                ? 'bg-[hsl(var(--card))] text-[#0088cc] shadow-sm ring-1 ring-[#0088cc]/20'
                : 'text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]'
            )}
          >
            <Icon className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">{label}</span>
          </button>
        );
      })}
    </div>
  );
}

/** Larger theme picker cards for Settings → Display */
export function ThemePreferenceCards({
  value,
  onChange,
}: {
  value: ThemePreference;
  onChange: (theme: ThemePreference) => void;
}) {
  const cards: Array<{
    value: ThemePreference;
    title: string;
    description: string;
    preview: 'light' | 'dark' | 'split';
  }> = [
    {
      value: 'light',
      title: 'Light',
      description: 'Bright workspace for daytime use',
      preview: 'light',
    },
    {
      value: 'dark',
      title: 'Dark',
      description: 'Low-glare interface for long sessions',
      preview: 'dark',
    },
    {
      value: 'auto',
      title: 'System',
      description: 'Follows your OS light or dark setting',
      preview: 'split',
    },
  ];

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
      {cards.map((card) => {
        const active = value === card.value;
        return (
          <button
            key={card.value}
            type="button"
            onClick={() => onChange(card.value)}
            className={cn(
              'group relative overflow-hidden rounded-2xl border text-left transition-all duration-200',
              active
                ? 'border-[#0088cc] bg-[#0088cc]/5 shadow-md shadow-[#0088cc]/10 ring-2 ring-[#0088cc]/25'
                : 'border-[hsl(var(--border))] bg-[hsl(var(--card))] hover:border-[#0088cc]/35 hover:shadow-sm'
            )}
            aria-pressed={active}
          >
            <div className="p-3 pb-2">
              <div
                className={cn(
                  'relative h-16 overflow-hidden rounded-xl border',
                  card.preview === 'light' && 'border-slate-200 bg-slate-50',
                  card.preview === 'dark' && 'border-slate-700 bg-[#0b1523]',
                  card.preview === 'split' && 'border-slate-300'
                )}
              >
                {card.preview === 'split' ? (
                  <div className="flex h-full">
                    <div className="w-1/2 bg-slate-50 p-2">
                      <div className="mb-1.5 h-1.5 w-8 rounded bg-slate-300" />
                      <div className="h-6 rounded bg-white shadow-sm ring-1 ring-slate-200/80" />
                    </div>
                    <div className="w-1/2 bg-[#0b1523] p-2">
                      <div className="mb-1.5 h-1.5 w-8 rounded bg-slate-600" />
                      <div className="h-6 rounded bg-[#132033] ring-1 ring-white/10" />
                    </div>
                  </div>
                ) : (
                  <div className="flex h-full gap-2 p-2">
                    <div
                      className={cn(
                        'w-4 rounded-md',
                        card.preview === 'light' ? 'bg-slate-200' : 'bg-[#132033]'
                      )}
                    />
                    <div className="flex-1 space-y-1.5 pt-0.5">
                      <div
                        className={cn(
                          'h-1.5 w-10 rounded',
                          card.preview === 'light' ? 'bg-slate-300' : 'bg-slate-600'
                        )}
                      />
                      <div
                        className={cn(
                          'h-7 rounded-md',
                          card.preview === 'light'
                            ? 'bg-white shadow-sm ring-1 ring-slate-200/80'
                            : 'bg-[#132033] ring-1 ring-white/10'
                        )}
                      />
                    </div>
                  </div>
                )}
                <div className="absolute right-2 top-2 h-2 w-2 rounded-full bg-[#0088cc]" />
              </div>
            </div>
            <div className="px-3 pb-3">
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-bold text-[hsl(var(--foreground))]">{card.title}</p>
                {active && (
                  <span className="rounded-full bg-[#0088cc] px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-white">
                    Active
                  </span>
                )}
              </div>
              <p className="mt-0.5 text-[11px] font-medium leading-snug text-[hsl(var(--muted-foreground))]">
                {card.description}
              </p>
            </div>
          </button>
        );
      })}
    </div>
  );
}
